import { useState, type FC } from 'react';
import { useScrollToTop } from '../hooks/useScrollToTop.js';
import { AlertIcon } from '../icons.js';
import type { RegFormData } from './types.js';

export interface StepProps {
  data: RegFormData;
  update: (patch: Partial<RegFormData>) => void;
  errors: Record<string, string>;
}
export interface WizardStep {
  key: string;
  title: string;
  validate: (data: RegFormData) => Record<string, string>;
  /** Optional async validation run on "Next" after `validate` passes (e.g. a
   *  server display-name check). Blocks advancing while any error is returned. */
  validateAsync?: (data: RegFormData) => Promise<Record<string, string>>;
  Component: FC<StepProps>;
}

export function RegistrationWizard({
  heading, getSteps, initialData, submitting, error, onSubmit, submitLabel = 'Submit',
}: {
  heading: string;
  getSteps: (data: RegFormData) => WizardStep[];
  initialData: RegFormData;
  submitting: boolean;
  error: string | null;
  onSubmit: (data: RegFormData) => void;
  submitLabel?: string;
}) {
  const [data, setData] = useState<RegFormData>(initialData);
  const [index, setIndex] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [checking, setChecking] = useState(false);

  const steps = getSteps(data);
  const clamped = Math.min(index, steps.length - 1);
  const step = steps[clamped];
  // Keyed on the step's identity, not the raw index: the guide wizard derives
  // its step list from the form data, so the same index can become a different
  // step.
  useScrollToTop(step.key);
  const isLast = clamped === steps.length - 1;
  const update = (patch: Partial<RegFormData>) => {
    setData((d) => ({ ...d, ...patch }));
    // Clear the error for any field being edited so fixed fields stop showing stale errors.
    setErrors((e) => {
      const keys = Object.keys(patch);
      if (!keys.some((k) => k in e)) return e;
      const nextErrs = { ...e };
      for (const k of keys) delete nextErrs[k];
      return nextErrs;
    });
  };

  async function next() {
    const errs = step.validate(data);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    if (step.validateAsync) {
      setChecking(true);
      try {
        const asyncErrs = await step.validateAsync(data);
        if (Object.keys(asyncErrs).length > 0) { setErrors(asyncErrs); return; }
      } finally {
        setChecking(false);
      }
    }
    if (isLast) onSubmit(data);
    else setIndex(clamped + 1);
  }
  function back() { setErrors({}); setIndex(Math.max(0, clamped - 1)); }

  const StepComponent = step.Component;
  return (
    <div className="max-w-xl mx-auto card p-7 lg:p-10 has-grain animate-fade-up">
      <div className="text-lg sm:text-xl font-display font-semibold tracking-[0.02em] uppercase text-ink-600 dark:text-[#b6b2c4] mb-2">{heading}</div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="h-card text-ink-900 dark:text-ink-100">{step.title}</h1>
        <span className="hint">Step {clamped + 1} of {steps.length}</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-ink-200 dark:bg-white/10 mb-6" role="progressbar"
           aria-valuenow={clamped + 1} aria-valuemin={1} aria-valuemax={steps.length}>
        <div className="h-1.5 rounded-full bg-brand transition-all"
             style={{ width: `${((clamped + 1) / steps.length) * 100}%` }} />
      </div>

      <div className="flex flex-col gap-4">
        <StepComponent data={data} update={update} errors={errors} />
      </div>

      {error ? (
        <div className="notice-error mt-5"><AlertIcon className="shrink-0 mt-0.5" /><span>{error}</span></div>
      ) : null}

      <div className="flex justify-between mt-7">
        <button type="button" className="btn-ghost" onClick={back} disabled={clamped === 0 || submitting || checking}>Back</button>
        <button type="button" className="btn-primary" onClick={next} disabled={submitting || checking}>
          {isLast ? (submitting ? 'Submitting…' : submitLabel) : (checking ? 'Checking…' : 'Next')}
        </button>
      </div>
    </div>
  );
}
